import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonBase, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import CursorDefaultOutlineIcon from 'mdi-react/CursorDefaultOutlineIcon';
import DoorOpenIcon from 'mdi-react/DoorOpenIcon';
import TextureBoxIcon from 'mdi-react/TextureBoxIcon';
import VectorSquareEditIcon from 'mdi-react/VectorSquareEditIcon';
import DeskIcon from 'mdi-react/DeskIcon';
import FormatTextIcon from 'mdi-react/FormatTextIcon';
import RectangleOutlineIcon from 'mdi-react/RectangleOutlineIcon';
import VectorPolygonIcon from 'mdi-react/VectorPolygonIcon';
import TableChairIcon from 'mdi-react/TableChairIcon';
import SignDirectionIcon from 'mdi-react/SignDirectionIcon';
import CursorMoveIcon from 'mdi-react/CursorMoveIcon';
import VectorPolylineEditIcon from 'mdi-react/VectorPolylineEditIcon';
import ArrowExpandHorizontalIcon from 'mdi-react/ArrowExpandHorizontalIcon';
import RotateLeftIcon from 'mdi-react/RotateLeftIcon';
import RotateRightIcon from 'mdi-react/RotateRightIcon';
import ArrowCollapseIcon from 'mdi-react/ArrowCollapseIcon';
import ArrowExpandIcon from 'mdi-react/ArrowExpandIcon';
import ContentDuplicateIcon from 'mdi-react/ContentDuplicateIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import LightbulbOnOutlineIcon from 'mdi-react/LightbulbOnOutlineIcon';
import GestureTapIcon from 'mdi-react/GestureTapIcon';
import type { PolygonFeature } from '../models/editor-feature';
import { isAreaMode, type EditMode } from '../models/edit-mode';
import {
  isCorneredTool, isDrawingTool, isPlacementTool, type DrawTarget, type EditorTool,
} from '../models/editor-tool';
import type { FloorPlanRoomOption } from '../models/floorplan-room';
import type { EditorDocument } from '../hooks/use-editor-document';
import type { ReshapeMode } from '../hooks/use-canvas-interaction';
import type { Selection } from '../hooks/use-selection';
import type { Scale } from '../utils/measurement';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';
import { BASIC_SHAPES } from './basic-shapes';
import ConfirmDialog from './confirm-dialog.component';
import RoomSelectField from './room-select-field.component';
import SplitPartsNotice from './split-parts-notice.component';

interface Props {
  readonly mode: EditMode;
  readonly tool: EditorTool;
  readonly selection: Selection;
  readonly document: EditorDocument;
  readonly scale: Scale;
  readonly reshape: ReshapeMode;
  readonly onReshapeChange: (reshape: ReshapeMode) => void;
  readonly rooms: FloorPlanRoomOption[];
  readonly storey: number;
  readonly roomIdsOnThisFloor: ReadonlySet<string>;
  readonly roomsUnavailable: boolean;
  /** Arms a drawing tool, landing what it draws on the given layer. */
  readonly onDraw: (tool: EditorTool, target: DrawTarget) => void;
  /** Arms a tool that places one thing where the plan is next clicked. */
  readonly onPlace: (tool: Extract<EditorTool, 'desk' | 'deskGroup' | 'text' | 'icon'>) => void;
  /** Disarms whatever tool is armed and drops anything half-drawn. */
  readonly onCancelTool: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
  /** Whether the plan has a scale, without which a desk group has no size to be laid out at. */
  readonly hasScale: boolean;
  /**
   * What to show for a selection this panel does not edit itself — a desk, a label, anything
   * picked with the plain pointer. The full properties inspector, as it has always been.
   */
  readonly children?: React.ReactNode;
}

const ROTATION_STEP = 15;
const SCALE_STEP = 1.1;

const MODE_META: Readonly<Record<EditMode, { titleKey: string; helpKey: string; Icon: typeof DeskIcon }>> = {
  select: { titleKey: 'floorplan-simple-mode-select', helpKey: 'floorplan-simple-panel-select-help', Icon: CursorDefaultOutlineIcon },
  rooms: { titleKey: 'floorplan-simple-mode-rooms', helpKey: 'floorplan-simple-panel-rooms-help', Icon: DoorOpenIcon },
  floor: { titleKey: 'floorplan-simple-mode-floor', helpKey: 'floorplan-simple-panel-floor-help', Icon: TextureBoxIcon },
  border: { titleKey: 'floorplan-simple-mode-border', helpKey: 'floorplan-simple-panel-border-help', Icon: VectorSquareEditIcon },
  desks: { titleKey: 'floorplan-simple-mode-desks', helpKey: 'floorplan-simple-panel-desks-help', Icon: DeskIcon },
  labels: { titleKey: 'floorplan-simple-mode-labels', helpKey: 'floorplan-simple-panel-labels-help', Icon: FormatTextIcon },
};

/** What to tell somebody who has just armed a tool, so the next click is not a guess. */
const instructionKeyOf = (tool: EditorTool): string | undefined => {
  if (tool === 'polygon') return 'floorplan-simple-armed-polygon';
  if (tool === 'line') return 'floorplan-simple-armed-line';
  if (isCorneredTool(tool)) return 'floorplan-simple-armed-shape';
  if (tool === 'text' || tool === 'icon') return 'floorplan-simple-armed-label';
  if (isPlacementTool(tool)) return 'floorplan-simple-armed-desk';
  return undefined;
};

/** A big, labelled action: the building block of every "Add" section. */
const ActionTile = ({
  label, Icon, onClick, disabled = false, active = false,
}: {
  readonly label: string;
  readonly Icon: typeof DeskIcon;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly active?: boolean;
}): React.JSX.Element => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={active}
    sx={{
      flex: '1 1 80px',
      minWidth: 80,
      py: 1.25,
      px: 0.5,
      flexDirection: 'column',
      gap: 0.5,
      borderRadius: `${editorTheme.radius}px`,
      border: `1px solid ${active ? editorTheme.accent : editorTheme.line}`,
      color: active ? editorTheme.accent : editorTheme.ink,
      backgroundColor: active ? editorTheme.accentSoft : editorTheme.surface,
      opacity: disabled ? 0.45 : 1,
      transition: 'border-color 120ms, background-color 120ms',
      '&:hover': { borderColor: editorTheme.accent, backgroundColor: editorTheme.accentSoft },
      '&.Mui-focusVisible': { boxShadow: editorTheme.focusRing },
    }}
  >
    <Icon size={24} style={{ color: active ? editorTheme.accent : editorTheme.inkDim }} />
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, color: 'inherit', textAlign: 'center' }}>
      {label}
    </Typography>
  </ButtonBase>
);

const Section = ({ title, children }: { readonly title: string; readonly children: React.ReactNode }): React.JSX.Element => (
  <Stack spacing={1} sx={{ px: 2, py: 1.5, borderTop: `1px solid ${editorTheme.lineSoft}` }}>
    <Typography sx={sectionHeadingSx}>{title}</Typography>
    {children}
  </Stack>
);

const Hint = ({ children }: { readonly children: React.ReactNode }): React.JSX.Element => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      p: 1,
      borderRadius: `${editorTheme.radiusSmall}px`,
      backgroundColor: editorTheme.surfaceSunken,
      color: editorTheme.inkDim,
    }}
  >
    <LightbulbOnOutlineIcon size={16} style={{ flexShrink: 0, marginTop: 1, color: editorTheme.warn }} />
    <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: 'inherit' }}>{children}</Typography>
  </Stack>
);

/**
 * The right-hand panel of the simple editor.
 *
 * Says which mode is on and what it is for, offers what can be added in that mode, and — once
 * something is clicked — every change that can be made to it, as named buttons rather than as
 * a form of numbers. Rooms, the floor and the building outline are edited here directly;
 * anything else hands over to the full properties inspector passed in as children.
 */
const ModePanel = ({
  mode,
  tool,
  selection,
  document: editorDocument,
  scale,
  reshape,
  onReshapeChange,
  rooms,
  storey,
  roomIdsOnThisFloor,
  roomsUnavailable,
  onDraw,
  onPlace,
  onCancelTool,
  onDuplicate,
  onDelete,
  hasScale,
  children,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { features, keys } = selection;
  const meta = MODE_META[mode];
  const instructionKey = instructionKeyOf(tool);

  const areas = features.filter((feature): feature is PolygonFeature => feature.kind === 'polygon');
  const single: PolygonFeature | undefined = features.length === 1 && areas.length === 1 ? areas[0] : undefined;
  const editsAreasHere = isAreaMode(mode) && features.length > 0 && areas.length === features.length;

  // The outline of the building is reshaped, never removed or copied: a plan without one has
  // nothing to say where the building ends, and a second one is two buildings.
  const isBorder = mode === 'border';

  const renderAdd = (): React.ReactNode => {
    switch (mode) {
      case 'rooms':
        return (
          <Section title={String(t('floorplan-simple-add-room'))}>
            <Stack direction="row" sx={{ gap: 1 }}>
              <ActionTile
                label={String(t('floorplan-simple-add-room-rectangle'))}
                Icon={RectangleOutlineIcon}
                active={tool === 'rectangle'}
                onClick={(): void => onDraw('rectangle', 'room')}
              />
              <ActionTile
                label={String(t('floorplan-simple-add-room-custom'))}
                Icon={VectorPolygonIcon}
                active={tool === 'polygon'}
                onClick={(): void => onDraw('polygon', 'room')}
              />
            </Stack>
          </Section>
        );
      case 'floor':
        return (
          <Section title={String(t('floorplan-simple-add-shape'))}>
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
              {BASIC_SHAPES.map(({ tool: shapeTool, labelKey, Icon }) => (
                <ActionTile
                  key={shapeTool}
                  label={String(t(labelKey))}
                  Icon={Icon}
                  active={tool === shapeTool}
                  onClick={(): void => onDraw(shapeTool, 'floor')}
                />
              ))}
              <ActionTile
                label={String(t('floorplan-simple-shape-custom'))}
                Icon={VectorPolygonIcon}
                active={tool === 'polygon'}
                onClick={(): void => onDraw('polygon', 'floor')}
              />
            </Stack>
          </Section>
        );
      case 'border':
        return (
          <Section title={String(t('floorplan-simple-border-title'))}>
            <Hint>{t('floorplan-simple-border-no-add')}</Hint>
          </Section>
        );
      case 'desks':
        return (
          <Section title={String(t('floorplan-simple-add-desk'))}>
            <Stack direction="row" sx={{ gap: 1 }}>
              <ActionTile
                label={String(t('floorplan-simple-add-desk-single'))}
                Icon={DeskIcon}
                active={tool === 'desk'}
                onClick={(): void => onPlace('desk')}
              />
              <ActionTile
                label={String(t('floorplan-simple-add-desk-group'))}
                Icon={TableChairIcon}
                active={tool === 'deskGroup'}
                disabled={!hasScale}
                onClick={(): void => onPlace('deskGroup')}
              />
            </Stack>
          </Section>
        );
      case 'labels':
        return (
          <Section title={String(t('floorplan-simple-add-label'))}>
            <Stack direction="row" sx={{ gap: 1 }}>
              <ActionTile
                label={String(t('floorplan-simple-add-label-text'))}
                Icon={FormatTextIcon}
                active={tool === 'text'}
                onClick={(): void => onPlace('text')}
              />
              <ActionTile
                label={String(t('floorplan-simple-add-label-icon'))}
                Icon={SignDirectionIcon}
                active={tool === 'icon'}
                onClick={(): void => onPlace('icon')}
              />
            </Stack>
          </Section>
        );
      default:
        return null;
    }
  };

  const reshapeOptions: ReadonlyArray<{ value: ReshapeMode; labelKey: string; Icon: typeof DeskIcon }> = [
    ...(isBorder ? [] : [{ value: 'off' as const, labelKey: 'floorplan-simple-reshape-move', Icon: CursorMoveIcon }]),
    { value: 'corners', labelKey: 'floorplan-simple-reshape-corners', Icon: VectorPolylineEditIcon },
    { value: 'sides', labelKey: 'floorplan-simple-reshape-sides', Icon: ArrowExpandHorizontalIcon },
  ];

  const titleOf = (area: PolygonFeature): string => {
    if (area.layer === 'room') {
      return rooms.find((room) => room.id === area.featureId)?.name
        ?? String(t('floorplan-simple-room-unnamed'));
    }

    if (area.layer === 'border') {
      return String(t('floorplan-simple-border-title'));
    }

    return String(t(area.centreline ? 'floorplan-simple-shape-line' : 'floorplan-simple-floor-shape'));
  };

  const quickAction = (
    label: string,
    Icon: typeof DeskIcon,
    onClick: () => void,
    tone: 'normal' | 'danger' = 'normal',
  ): React.JSX.Element => (
    <Button
      key={label}
      size="small"
      variant="outlined"
      color={tone === 'danger' ? 'error' : 'inherit'}
      startIcon={<Icon size={16} />}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        justifyContent: 'flex-start',
        borderColor: tone === 'danger' ? undefined : editorTheme.line,
        color: tone === 'danger' ? undefined : editorTheme.ink,
      }}
    >
      {label}
    </Button>
  );

  const renderAreaEditor = (): React.ReactNode => {
    const linked = single?.layer === 'room' && rooms.some((room) => room.id === single.featureId);

    return (
      <>
        <Section title={String(t('floorplan-simple-selected'))}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 15, fontWeight: 650, color: editorTheme.ink, flexGrow: 1, minWidth: 0 }} noWrap>
              {single ? titleOf(single) : t('floorplan-simple-selected-many', { count: features.length })}
            </Typography>
            {single?.layer === 'room' && !roomsUnavailable && (
              <Chip
                size="small"
                color={linked ? 'success' : 'warning'}
                label={String(t(linked ? 'floorplan-simple-room-linked' : 'floorplan-simple-room-not-linked'))}
              />
            )}
          </Stack>

          {/* A room is bookable because its outline carries the room's identifier. Asked for
              first, because a room drawn and never linked looks finished and books nobody. */}
          {single?.layer === 'room' && !roomsUnavailable && (
            <RoomSelectField
              rooms={rooms}
              storey={storey}
              value={linked ? single.featureId : ''}
              roomIdsOnThisFloor={roomIdsOnThisFloor}
              helperText={String(t(linked ? 'floorplan-editor-zone-room-hint' : 'floorplan-simple-room-link-help'))}
              onChange={(roomId): void => editorDocument.updateProperties<PolygonFeature>(
                keys,
                'polygon',
                { featureId: roomId },
              )}
            />
          )}
        </Section>

        {single && (
          <Section title={String(t('floorplan-simple-reshape-title'))}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={reshape}
              onChange={(_, next: ReshapeMode | null): void => {
                if (next) onReshapeChange(next);
              }}
              aria-label={String(t('floorplan-simple-reshape-title'))}
              sx={{
                '& .MuiToggleButton-root': {
                  flexDirection: 'column',
                  gap: 0.25,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  color: editorTheme.inkDim,
                  borderColor: editorTheme.line,
                  '&.Mui-selected': {
                    color: editorTheme.accent,
                    backgroundColor: editorTheme.accentSoft,
                    '&:hover': { backgroundColor: editorTheme.accentSoft },
                  },
                },
              }}
            >
              {reshapeOptions.map(({ value, labelKey, Icon }) => (
                <ToggleButton key={value} value={value} aria-label={String(t(labelKey))}>
                  <Icon size={20} />
                  {t(labelKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Hint>{t(`floorplan-simple-reshape-${reshape}-help`)}</Hint>

            <SplitPartsNotice
              coordinates={single.coordinates}
              onSplit={(): void => selection.selectKeys(editorDocument.splitParts(single.id))}
            />
          </Section>
        )}

        {/* Only for a line the editor drew: its thickness is editable because the line it was
            swept from is still on the feature. */}
        {single?.centreline && single.thickness !== null && (
          <Section title={String(t('floorplan-simple-line-thickness'))}>
            <TextField
              size="small"
              type="number"
              value={Math.round(single.thickness * 100)}
              onChange={(event): void => {
                const centimetres = Number(event.target.value);

                if (Number.isFinite(centimetres) && centimetres > 0) {
                  editorDocument.setThickness(single.id, centimetres / 100, scale);
                }
              }}
              InputProps={{
                endAdornment: <Typography sx={{ fontSize: 12, color: editorTheme.inkFaint }}>cm</Typography>,
                inputProps: { min: 1, step: 5, 'aria-label': String(t('floorplan-simple-line-thickness')) },
              }}
            />
          </Section>
        )}

        <Section title={String(t('floorplan-simple-actions'))}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {quickAction(String(t('floorplan-simple-rotate-left')), RotateLeftIcon, () => editorDocument.rotateBy(keys, -ROTATION_STEP))}
            {quickAction(String(t('floorplan-simple-rotate-right')), RotateRightIcon, () => editorDocument.rotateBy(keys, ROTATION_STEP))}
            {quickAction(String(t('floorplan-simple-smaller')), ArrowCollapseIcon, () => editorDocument.scaleBy(keys, 1 / SCALE_STEP))}
            {quickAction(String(t('floorplan-simple-bigger')), ArrowExpandIcon, () => editorDocument.scaleBy(keys, SCALE_STEP))}
            {!isBorder && quickAction(String(t('floorplan-simple-duplicate')), ContentDuplicateIcon, onDuplicate)}
            {!isBorder && quickAction(
              String(t(mode === 'rooms' ? 'floorplan-simple-remove-room' : 'floorplan-simple-remove-shape')),
              TrashCanOutlineIcon,
              () => setConfirmingDelete(true),
              'danger',
            )}
          </Box>
        </Section>

        <ConfirmDialog
          open={confirmingDelete}
          messageKey={mode === 'rooms' ? 'floorplan-simple-remove-room-confirm' : 'floorplan-simple-remove-shape-confirm'}
          onConfirm={(): void => {
            setConfirmingDelete(false);
            onDelete();
          }}
          onCancel={(): void => setConfirmingDelete(false)}
        />
      </>
    );
  };

  const renderEmpty = (): React.ReactNode => (
    <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 4, textAlign: 'center', color: editorTheme.inkFaint }}>
      <GestureTapIcon size={36} />
      <Typography sx={{ fontSize: 13, color: editorTheme.inkDim }}>
        {t(`floorplan-simple-empty-${mode}`)}
      </Typography>
    </Stack>
  );

  const renderSelection = (): React.ReactNode => {
    if (editsAreasHere) {
      return renderAreaEditor();
    }

    if (features.length > 0) {
      return <Box sx={{ borderTop: `1px solid ${editorTheme.lineSoft}` }}>{children}</Box>;
    }

    return renderEmpty();
  };

  return (
    <Stack sx={{ minHeight: '100%' }} aria-live="polite">
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${editorTheme.radius}px`,
            color: editorTheme.accent,
            backgroundColor: editorTheme.accentSoft,
          }}
        >
          <meta.Icon size={20} />
        </Box>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: editorTheme.ink }}>{t(meta.titleKey)}</Typography>
          <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: editorTheme.inkDim }}>{t(meta.helpKey)}</Typography>
        </Stack>
      </Stack>

      {/* A tool is armed: say what the next click does, and offer the way out. */}
      {instructionKey && (
        <Stack
          spacing={1}
          sx={{
            mx: 2,
            mb: 1.5,
            p: 1.25,
            borderRadius: `${editorTheme.radius}px`,
            border: `1px solid ${editorTheme.accent}`,
            backgroundColor: editorTheme.accentSoft,
          }}
          role="status"
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: editorTheme.ink }}>
            {t(instructionKey)}
          </Typography>
          <Stack direction="row" justifyContent="flex-end">
            <Button size="small" onClick={onCancelTool} sx={{ textTransform: 'none' }}>
              {t(isDrawingTool(tool) ? 'floorplan-simple-cancel-drawing' : 'cancel')}
            </Button>
          </Stack>
        </Stack>
      )}

      {renderAdd()}
      {renderSelection()}
    </Stack>
  );
};

export default ModePanel;
